import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { 
  FeatherSearch,
  FeatherFilter,
  FeatherGrid,
  FeatherList,
  FeatherX
} from '../ui/icons';
import { useMasterDataArray } from '../../hooks/useMasterData';

const SearchAndFilters = ({ filters, onFiltersChange, users, viewMode, onViewModeChange, metrics }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [showTechStackDropdown, setShowTechStackDropdown] = useState(false);
  const [techStackSearch, setTechStackSearch] = useState('');
  const techStackRef = useRef(null);

  // Color scheme for quick filter buttons
  const getFilterButtonStyle = (status, isActive) => {
    const baseColors = {
      '': { bg: '#6B7280', hover: '#4B5563', text: '#FFFFFF' }, // All - Gray
      'initial_data_entered': { bg: '#8B5CF6', hover: '#7C3AED', text: '#FFFFFF' }, // New - Purple
      'contacted': { bg: '#F59E0B', hover: '#D97706', text: '#FFFFFF' }, // Contacted - Amber
      'in_discussion': { bg: '#3B82F6', hover: '#2563EB', text: '#FFFFFF' }, // In Discussion - Blue
      'ready_to_engage': { bg: '#10B981', hover: '#059669', text: '#FFFFFF' }, // Ready - Green
      'onboarding_in_progress': { bg: '#10B981', hover: '#059669', text: '#FFFFFF' }, // Onboarding - Green
      'fully_onboarded': { bg: '#059669', hover: '#047857', text: '#FFFFFF' } // Fully Onboarded - Dark Green
    };

    const colors = baseColors[status] || baseColors[''];
    
    if (isActive) {
      return {
        backgroundColor: colors.bg,
        color: colors.text,
        borderColor: colors.bg,
        boxShadow: `0 2px 8px ${colors.bg}40`
      };
    }
    
    return {
      backgroundColor: 'transparent',
      color: colors.bg,
      borderColor: colors.bg,
      boxShadow: 'none'
    };
  };

  // const statusOptions = [
  //   { value: '', label: 'All Statuses' },
  //   { value: 'initial_data_entered', label: 'Initial Data Entered' },
  //   { value: 'contacted', label: 'Contacted' },
  //   { value: 'in_discussion', label: 'In Discussion' },
  //   { value: 'awaiting_documents', label: 'Awaiting Documents' },
  //   { value: 'agreement_signed', label: 'Agreement Signed' },
  //   { value: 'rejected_not_interested', label: 'Rejected/Not Interested' }
  // ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'initial_data_entered', label: 'Initial Data Entered' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'in_discussion', label: 'In Discussion' },
    { value: 'ready_to_engage', label: 'Ready to Engage' },
    { value: 'onboarding_in_progress', label: 'Onboarding in Progress' },
    { value: 'fully_onboarded', label: 'Fully Onboarded' },
    { value: 'not_interested', label: 'Not Interested' },
    { value: 'not_eligible', label: 'Not Eligible' },
  ];

  const industryOptions = [
    { value: '', label: 'All Industries' },
    { value: 'Technology', label: 'Technology' },
    { value: 'Healthcare', label: 'Healthcare' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Education', label: 'Education' },
    { value: 'Manufacturing', label: 'Manufacturing' },
    { value: 'Retail', label: 'Retail' },
    { value: 'Consulting', label: 'Consulting' },
    { value: 'Startup', label: 'Startup' }
  ];

  // Master data hook for tech stack options
  const { values: techStackValues, loading: techStackLoading } = useMasterDataArray('technology_stack');
  
  // Convert tech stack values to options format
  const techStackOptions = [
    { value: '', label: 'All Tech Stacks' },
    ...(techStackValues || []).map(value => ({ value, label: value }))
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleTechStackToggle = (techStack) => {
    const currentTechStacks = filters.tech_stack ? filters.tech_stack.split(',').filter(t => t.trim()) : [];
    
    if (currentTechStacks.includes(techStack)) {
      // Remove from selection
      const updatedTechStacks = currentTechStacks.filter(t => t !== techStack);
      handleFilterChange('tech_stack', updatedTechStacks.join(','));
    } else {
      // Add to selection
      const updatedTechStacks = [...currentTechStacks, techStack];
      handleFilterChange('tech_stack', updatedTechStacks.join(','));
    }
  };

  const getSelectedTechStacks = () => {
    return filters.tech_stack ? filters.tech_stack.split(',').filter(t => t.trim()) : [];
  };

  const removeTechStack = (techToRemove) => {
    const currentTechStacks = getSelectedTechStacks();
    const updatedTechStacks = currentTechStacks.filter(t => t !== techToRemove);
    handleFilterChange('tech_stack', updatedTechStacks.join(','));
  };

  const getFilteredTechStackOptions = () => {
    if (!techStackSearch.trim()) {
      return techStackOptions.slice(1); // Exclude "All Tech Stacks" option
    }
    return techStackOptions.slice(1).filter(option => 
      option.label.toLowerCase().includes(techStackSearch.toLowerCase())
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (techStackRef.current && !techStackRef.current.contains(event.target)) {
        setShowTechStackDropdown(false);
        setTechStackSearch(''); // Clear search when closing
      }
    };

    if (showTechStackDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTechStackDropdown]);

  const clearFilters = () => {
    onFiltersChange({
      status: '',
      industry: '',
      assigned_to: '',
      created_by: '',
      search: '',
      willing_to_work: false,
      tech_stack: ''
    });
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'willing_to_work') {
      return value === true;
    }
    return value !== '';
  });

  return (
    <div className="search-filters">
      {/* Search Bar */}
      <div className="search-bar">
        <div className="search-input-wrapper">
          <FeatherSearch size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search companies, websites, or emails..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
          {filters.search && (
            <Button
              variant="neutral-tertiary"
              size="sm"
              icon={<FeatherX />}
              onClick={() => handleFilterChange('search', '')}
              className="clear-search"
            />
          )}
        </div>
        
        <div className="search-actions">
          <Button
            variant={showFilters ? "brand-primary" : "neutral-tertiary"}
            icon={<FeatherFilter />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters {hasActiveFilters && `(${Object.entries(filters).filter(([key, value]) => {
              if (key === 'willing_to_work') {
                return value === true;
              }
              return value !== '';
            }).length})`}
          </Button>
          
          <div className="view-controls">
            <button
              className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => onViewModeChange('grid')}
            >
              <FeatherGrid size={16} />
            </button>
            <button
              className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => onViewModeChange('list')}
            >
              <FeatherList size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Quick Status Filter Buttons */}
      <div className="quick-status-filters">
        <button
          type="button"
          onClick={() => handleFilterChange('status', '')}
          className="quick-filter-btn"
          style={getFilterButtonStyle('', filters.status === '')}
        >
          All
          {metrics?.status_breakdown && (
            <span className="filter-count">
              {Object.values(metrics.status_breakdown).reduce((sum, count) => sum + count, 0)}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => handleFilterChange('status', 'initial_data_entered')}
          className="quick-filter-btn"
          style={getFilterButtonStyle('initial_data_entered', filters.status === 'initial_data_entered')}
        >
          New
          {metrics?.status_breakdown?.initial_data_entered && (
            <span className="filter-count">
              {metrics.status_breakdown.initial_data_entered}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => handleFilterChange('status', 'contacted')}
          className="quick-filter-btn"
          style={getFilterButtonStyle('contacted', filters.status === 'contacted')}
        >
          Contacted
          {metrics?.status_breakdown?.contacted && (
            <span className="filter-count">
              {metrics.status_breakdown.contacted}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => handleFilterChange('status', 'in_discussion')}
          className="quick-filter-btn"
          style={getFilterButtonStyle('in_discussion', filters.status === 'in_discussion')}
        >
          In Discussion
          {metrics?.status_breakdown?.in_discussion && (
            <span className="filter-count">
              {metrics.status_breakdown.in_discussion}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => handleFilterChange('status', 'ready_to_engage')}
          className="quick-filter-btn"
          style={getFilterButtonStyle('ready_to_engage', filters.status === 'ready_to_engage' || filters.status === 'onboarding_in_progress')}
        >
          Ready & Onboarding
          {metrics?.status_breakdown && (
            <span className="filter-count">
              {(metrics.status_breakdown.ready_to_engage || 0) + (metrics.status_breakdown.onboarding_in_progress || 0)}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => handleFilterChange('status', 'fully_onboarded')}
          className="quick-filter-btn"
          style={getFilterButtonStyle('fully_onboarded', filters.status === 'fully_onboarded')}
        >
          Fully Onboarded
          {metrics?.status_breakdown?.fully_onboarded && (
            <span className="filter-count">
              {metrics.status_breakdown.fully_onboarded}
            </span>
          )}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-row">
            <div className="filter-group">
              <label className="filter-label">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="filter-select"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Industry</label>
              <select
                value={filters.industry}
                onChange={(e) => handleFilterChange('industry', e.target.value)}
                className="filter-select"
              >
                {industryOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Assigned To</label>
              <select
                value={filters.assigned_to}
                onChange={(e) => handleFilterChange('assigned_to', e.target.value)}
                className="filter-select"
              >
                <option value="">All Users</option>
                <option value="unassigned">Unassigned</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.display_name || user.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Created By</label>
              <select
                value={filters.created_by}
                onChange={(e) => handleFilterChange('created_by', e.target.value)}
                className="filter-select"
              >
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.display_name || user.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Tech Stack</label>
              <div className="tech-stack-multiselect" ref={techStackRef}>
                <button
                  type="button"
                  className="tech-stack-trigger"
                  onClick={() => setShowTechStackDropdown(!showTechStackDropdown)}
                >
                  <span className="tech-stack-display">
                    {getSelectedTechStacks().length === 0 
                      ? 'Select technologies...' 
                      : `${getSelectedTechStacks().length} selected`
                    }
                  </span>
                  <span className="tech-stack-arrow">▼</span>
                </button>
                
                {showTechStackDropdown && (
                  <div className="tech-stack-dropdown">
                    <div className="tech-stack-search">
                      <input
                        type="text"
                        placeholder="Search technologies..."
                        value={techStackSearch}
                        onChange={(e) => setTechStackSearch(e.target.value)}
                        className="tech-stack-search-input"
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="tech-stack-options">
                      {getFilteredTechStackOptions().map(option => {
                        const isSelected = getSelectedTechStacks().includes(option.value);
                        return (
                          <label key={option.value} className="tech-stack-option">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleTechStackToggle(option.value)}
                              className="tech-stack-checkbox"
                            />
                            <span className="tech-stack-label">{option.label}</span>
                          </label>
                        );
                      })}
                    </div>
                    {getSelectedTechStacks().length > 0 && (
                      <div className="tech-stack-actions">
                        <button
                          type="button"
                          className="tech-stack-clear"
                          onClick={() => handleFilterChange('tech_stack', '')}
                        >
                          Clear All
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">Willing to Work</label>
              <div className="filter-checkbox">
                <input
                  type="checkbox"
                  id="willing-to-work-filter"
                  checked={filters.willing_to_work || false}
                  onChange={(e) => handleFilterChange('willing_to_work', e.target.checked)}
                  className="filter-checkbox-input"
                />
                <label htmlFor="willing-to-work-filter" className="filter-checkbox-label">
                  Show only willing partners
                </label>
              </div>
            </div>

            {hasActiveFilters && (
              <div className="filter-actions">
                <Button
                  variant="neutral-tertiary"
                  size="sm"
                  onClick={clearFilters}
                >
                  Clear All
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selected Tech Stack Badges */}
      {getSelectedTechStacks().length > 0 && (
        <div className="selected-tech-badges">
          <div className="selected-tech-header">
            <span className="selected-tech-label">Filtering by:</span>
          </div>
          <div className="selected-tech-list">
            {getSelectedTechStacks().map(tech => (
              <div key={tech} className="selected-tech-badge">
                <span className="selected-tech-name">{tech}</span>
                <button
                  type="button"
                  className="selected-tech-remove"
                  onClick={() => removeTechStack(tech)}
                  aria-label={`Remove ${tech} from filter`}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="clear-all-techs"
              onClick={() => handleFilterChange('tech_stack', '')}
            >
              Clear All
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchAndFilters; 